import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { SendFeedbackWebComponent } from '../component/SendFeedbackWebComponent';
import { SendFeedbackEmailWebView } from '../view/SendFeedbackEmailWebView';
import { templateSendFeedbackEmailType } from '../view/template';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { SendFeedbackEmailConstants } from '../SendFeedbackEmailConstants';

export default class SendFeedbackWebController extends BaseWebComponentController {
  public view: SendFeedbackEmailWebView;

  constructor(public component: SendFeedbackWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: SendFeedbackEmailWebView,
      viewTemplate: templateSendFeedbackEmailType,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        event: 'click',
        element: this.view.dom.$button,
        callback: () => this.sendFeedbackEmail(this.view.getFeedback()),
      },
      {
        event: 'mouseover',
        element: this.view.dom.$feedbackCard,
        callback: () =>
          (this.view.dom.$input.placeholder = this.view.getTranslations().sendFeedback.feedbackHoverDescription),
      },
      {
        event: 'mouseout',
        element: this.view.dom.$feedbackCard,
        callback: () =>
          (this.view.dom.$input.placeholder = this.view.getTranslations().sendFeedback.feedbackDescription),
      },
      {
        element: this.view.dom.$input,
        event: 'change',
        callback: this.onInputChanged,
      },
    ];
  }

  private onInputChanged(ev: WebComponentEvent): void {
    const userFeedback = ev.detail.sourceEvent;
    const currentLength = userFeedback.length;
    this.view.dom.$button.disabled = !userFeedback.trim() || currentLength > SendFeedbackEmailConstants.Input.MaxLength;
    this.view.dom.$wordCounter.innerText =
      currentLength.toString() +
      '/' +
      SendFeedbackEmailConstants.Input.MaxLength +
      ' ' +
      this.dependencies.translateFn('chars');
    this.view.dom.$wordCounter.style.color =
      currentLength >= SendFeedbackEmailConstants.Input.MaxLength ? 'red' : 'var(--body-text-arrows-400)';
    if (currentLength > SendFeedbackEmailConstants.Input.MaxLength) {
      this.view.dom.$input.markAsInvalid();
    } else {
      this.view.dom.$input.markAsValid();
    }
  }

  private showSuccessToast(successMessage: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement(
      {
        message: successMessage,
        severity: ToastSeverity.SUCCESS,
        x: ToastPositionX.RIGHT,
        y: ToastPositionY.BOTTOM,
      },
      undefined,
      false
    );
  }

  private showErrorToast(errorMessage: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement(
      {
        message: errorMessage,
        severity: ToastSeverity.ERROR,
        keepOpen: true,
        x: ToastPositionX.RIGHT,
        y: ToastPositionY.BOTTOM,
      },
      undefined,
      false
    );
  }

  async sendFeedbackEmail(feedback: string): Promise<void> {
    if (!feedback || feedback.length > SendFeedbackEmailConstants.Input.MaxLength) {
      return;
    }
    const response = await this.dependencies.api.clients.SensorsIntegrations.sendFeedbackEmail(
      { text: feedback },
      getRequestDefaultOptions()
    );
    if (response.success) {
      this.showSuccessToast(this.dependencies.translateFn('Feedback sent successfully.'));
    } else {
      if (response.data) {
        this.showErrorToast(response.data);
      } else {
        this.showErrorToast(this.dependencies.translateFn('Something went wrong...'));
      }
    }
  }
}
